/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiLink,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { userAuthenticationUrl } from '@/apps/userconsole/urls'

import { messages } from '../messages'

interface Props {
  roleMappingName: string
  onRemoveRoleMapping: () => void
  onClose: () => void
  isLoading: boolean
  hasToRunValidation: boolean
  samlSsoEnabled: boolean
}

const ConfirmRemovingRoleMappingModal = ({
  roleMappingName,
  onRemoveRoleMapping,
  onClose,
  isLoading,
  hasToRunValidation,
  samlSsoEnabled,
}: Props) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const modalTitleId = useGeneratedHtmlId()
  return (
    <EuiModal onClose={onClose} maxWidth='600px' aria-labelledby={modalTitleId}>
      <EuiModalHeader>
        <EuiModalHeaderTitle id={modalTitleId}>
          <FormattedMessage {...messages.modalRemoveRoleMappingTitle} />
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiText size='s'>
          <FormattedMessage
            {...messages.modalRemoveRoleMappingMessage}
            values={{
              roleMappingName,
              b: (msg) => <strong>{msg}</strong>,
            }}
          />
        </EuiText>
        <EuiSpacer size='m' />
        {hasToRunValidation && (
          <EuiCallOut
            size='s'
            color='warning'
            title={<FormattedMessage {...messages.modalRemoveOrgOwnerRoleMappingWarningTitle} />}
            iconType='warning'
          >
            <EuiText size='s'>
              <p>
                <FormattedMessage {...messages.modalRemoveOrgOwnerRoleMappingWarningDescription} />
              </p>
              {!samlSsoEnabled && (
                <p>
                  <FormattedMessage
                    {...messages.samlSsoIsNotConfigured}
                    values={{
                      link: (content) => (
                        <EuiLink onClick={() => navigate(userAuthenticationUrl())}>
                          {content}
                        </EuiLink>
                      ),
                    }}
                  />
                </p>
              )}
            </EuiText>
          </EuiCallOut>
        )}
      </EuiModalBody>
      <EuiModalFooter>
        <EuiFlexGroup justifyContent='flexEnd'>
          <EuiButtonEmpty onClick={onClose}>
            <FormattedMessage {...messages.modalRemoveRoleMappingCancel} />
          </EuiButtonEmpty>
          <EuiButton
            color='danger'
            fill={true}
            disabled={!samlSsoEnabled && hasToRunValidation}
            isLoading={isLoading}
            onClick={onRemoveRoleMapping}
            aria-label={formatMessage(messages.modalRemoveRoleMappingConfirm)}
          >
            <FormattedMessage {...messages.modalRemoveRoleMappingConfirm} />
          </EuiButton>
        </EuiFlexGroup>
      </EuiModalFooter>
    </EuiModal>
  )
}

export default ConfirmRemovingRoleMappingModal
